import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import MaterialDialogContent from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/DialogContent';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import RichTextDisplay from 'Components/UI/RichTextDisplay';
import Typography from 'Components/UI/Typography';
import {
  expenseHref,
  incomeDetailsHref,
  liabilitiesHref,
} from 'Consts/clientUris';
import useMobileLayout from 'Hooks/useMobileLayout';
import { useBalanceSheetPageURL } from 'Hooks/useBalanceSheetPageURL';

const scoreCalculationLinks = {
  'emergency-fund': {
    hidden: false,
    linkTitle: 'There are a few places you can enter more information to complete this number:',
    links: [
      {
        label: 'Capture your expenses',
        url: expenseHref,
      },
      {
        label: 'Link your bank and credit union accounts',
        linkToBalanceSheetPage: true,
      },
    ],
  },
  'savings-rate': {
    hidden: false,
    linkTitle: 'Inform this number by:',
    links: [
      {
        label: 'Capture your monthly savings',
        url: expenseHref,
      },
      {
        label: 'Capture your income',
        url: incomeDetailsHref,
      },
    ],
  },
  'withdrawal-rate': {
    hidden: true,
    linkTitle: null,
    links: [],
  },
  'leverage-score': {
    hidden: false,
    linkTitle: 'Inform this number by:',
    links: [
      {
        label: 'Linking your bank and investment accounts',
        linkToBalanceSheetPage: true,
      },
      {
        label: 'Linking your debt accounts',
        linkToBalanceSheetPage: true,
      },
    ],
  },
  'debt-score': {
    hidden: false,
    linkTitle: 'There are a few places you can enter more information to complete this number:',
    links: [
      {
        label: 'Capture your monthly debt expenses',
        url: expenseHref,
      },
      {
        label: 'Add your mortgage',
        linkToBalanceSheetPage: true,
      },
      {
        label: 'Capture your income',
        url: incomeDetailsHref,
      },
    ],
  },
  'credit-score': {
    hidden: false,
    linkTitle: '',
    links: [
      {
        label: 'Capture your credit score',
        url: liabilitiesHref,
      },
    ],
  },
};

const styles = theme => ({
  root: {
    padding: theme.spacing(0, 3, 3),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  row: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  valuesRow: {
    paddingBottom: theme.spacing(3),
  },
  value: {
    padding: theme.spacing(1, 2),
    textAlign: 'center',
  },
  valueValue: {
    fontWeight: theme.typography.fontWeightRegular,
  },
  valueLabel: {},
  description: {
    fontSize: theme.typography.h6,
    paddingRight: theme.spacing(1),
    color: theme.palette.facet.primaryGray,
  },
  image: {
    paddingBottom: theme.spacing(4),
  },
  imageDesktop: {
    width: theme.dimensions.md,
  },
  imageMobile: {
    width: theme.dimensions.sm,
  },
  calcLinks: {
    paddingTop: theme.spacing(4),
    width: '100%',
  },
  link: {
    display: 'block',
  },
});

const DialogContent = ({
  className,
  classes,
  componentResource,
}) => {
  const isMobile = useMobileLayout();
  const wellnessScoreValues = componentResource.attr('values').resources().list();
  const linkConfig = scoreCalculationLinks[componentResource.attr('type')];
  const balanceSheetPageURL = useBalanceSheetPageURL();

  return (
    <MaterialDialogContent
      className={classNames(classes.root, className)}
      disableTypography
    >
      <div className={classNames(classes.row, classes.valuesRow)}>
        {wellnessScoreValues.map((valueResource) => {
          let value = valueResource.attr('value');
          if (!value || value.length === 0) {
            value = '-';
          }
          return (
            <div className={classes.value}>
              <Typography
                className={classes.valueValue}
                color="secondary"
                component="span"
                variant="h1"
              >
                {value}
              </Typography>
              <Typography
                className={classes.valueLabel}
                color="primaryGray"
                variant="h6"
              >
                {valueResource.attr('label')}
              </Typography>
            </div>
          );
        })}
      </div>
      <img
        className={classNames(classes.image, {
          [classes.imageDesktop]: !isMobile,
          [classes.imageMobile]: isMobile,
        })}
        src={componentResource.relationship('image').link('related').url()}
      />
      <RichTextDisplay
        className={classes.description}
        content={componentResource.attr('description')}
      />
      {(linkConfig.hidden) ? null : (
        <div className={classes.calcLinks}>
          <Typography
            color="primaryGray"
            variant="h5"
          >
            {linkConfig.linkTitle}
          </Typography>
          {
            linkConfig.links.map((link) => {
              return (
                <Typography
                  className={classes.link}
                  variant="h5"
                  color="primary"
                  component={Link}
                  to={link.linkToBalanceSheetPage ? balanceSheetPageURL : link.url}
                >
                  {link.label}
                </Typography>
              );
            })
          }
        </div>
      )}
    </MaterialDialogContent>
  );
};

DialogContent.defaultProps = {
  className: '',
};

DialogContent.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  componentResource: PropTypes.object.isRequired,
};

export default withStyles(styles)(DialogContent);
